import React from 'react'
import styled from 'styled-components'
import { Col, Row } from 'react-styled-flexboxgrid'

import Layout from '../components/Layout'
import Helmet from '../components/Helmet'
import media from 'css-in-js-media'
import { BodyCopy, H2 } from '../components/Typography'
import { Variables } from '../styles/Variables'

import Button from '../components/Button'
import Hero from '../components/Hero'
import Frame from '../components/Frame'
import BlockText from '../components/BlockText'
import BlockImage from '../components/BlockImage'
import { StyledTitle } from '../components/Title'
import ContactFormSection from '../components/ContactFormSection'

const BannerAvailability = require('../svg/BannerAvailability2x.jpg') as string
const BannerGoldenAge = require('../svg/BannerGoldenAge2x.jpg') as string
const BannerAmenities = require('../svg/BannerAmenities2x.jpg') as string
const BannerDoorStep = require('../svg/BannerDoorStep2x.jpg') as string
const BannerOurVision = require('../svg/BannerOurVision2x.jpg') as string
const AndermattMap = require('../svg/Map_Image.png') as string

const Wrapper = styled.div``

const StyledH2 = styled(H2)`
    margin-top: 0;
    margin-bottom: 20px;
    ${media('<desktop')} {
        margin-top: 40px !important;
    }
`

const MapBlockText = styled(BlockText)`
    &:before,
    &:after {
        content: '';
        width: 1px;
        height: 653px;
        background: #c8965a;
        position: absolute;

        ${media('<desktop')} {
            width: 335px;
            height: 1px;
            left: 50%;
            transform: translateX(-50%);
        }
    }
    &:after {
        right: 0;
        ${media('<desktop')} {
            top: 0;
            right: initial;
        }
    }
    &:before {
        right: 7px;
        ${media('<desktop')} {
            top: 7px;
            right: initial;
        }
    }
    ${StyledTitle} {
        text-align: center;
        margin: 0 auto;
        max-width: 473px;

        ${media('<desktop')} {
            max-width: 100%;
        }
    }
    ${BodyCopy} {
        font-family: ${Variables.font.body};
        font-size: 20px;
        line-height: 120%;
        text-transform: initial;
        letter-spacing: initial;

        ${media('<desktop')} {
            font-size: 16px;
            max-width: 334px;
            margin: 0 auto;
            padding-bottom: 77px;
        }

        p {
            font-family: ${Variables.font.body};
        }

        h2 {
            font-family: ${Variables.font.title};
            font-weight: 300;
            font-size: 28px;
            line-height: 50px;
            margin-bottom: 0;
            ${media('<desktop')} {
                font-size: 18px;
            }
        }
    }
    ${H2} {
        margin-top: 0;
        margin-bottom: 12px;
        ${media('<desktop')} {
            font-size: 28px;
        }
    }
`

const ReverseRow = styled(Row)`
    ${media('<desktop')} {
        flex-direction: column-reverse;
    }
`

const MapBlockImage = styled.img`
    max-width: 100%;
`

export const TeaserTemplate: React.FC<{
    data?: object
    location?: object
}> = ({ data, location }) => {
    const teaserData = data.contentfulTeaser

    return (
        <Layout location={location} data={data}>
            <Helmet
                title={teaserData.metaInformation.pageTitle}
                description={
                    teaserData.metaInformation.description &&
                    teaserData.metaInformation.description.childMarkdownRemark
                        .html
                }
            ></Helmet>
            <Wrapper>
                {/* Content Blocks */}
                {teaserData.contentBlocks.map((item, idx) => (
                    <div key={idx}>
                        {idx == 0 && (
                            <Hero
                                title={item.title}
                                description={item.copy.childMarkdownRemark.html}
                            />
                        )}

                        {idx == 1 && (
                            <ReverseRow>
                                <Frame
                                    mobileWidth={12}
                                    width={6}
                                    data={
                                        <>
                                            <BodyCopy
                                                dangerouslySetInnerHTML={{
                                                    __html: item.title,
                                                }}
                                            />
                                            <H2
                                                dangerouslySetInnerHTML={{
                                                    __html: item.copy.childMarkdownRemark.html
                                                        .replace(
                                                            'CHF',
                                                            '\nCHF '
                                                        )
                                                        .replace(/h2>/g, 'p>'),
                                                }}
                                            />
                                        </>
                                    }
                                />
                                <BlockImage
                                    mobileWidth={12}
                                    width={6}
                                    bgImage={BannerAvailability}
                                />
                            </ReverseRow>
                        )}

                        {idx == 2 && (
                            <Row>
                                <BlockImage
                                    mobileWidth={12}
                                    width={6}
                                    bgImage={BannerGoldenAge}
                                />
                                <BlockText
                                    mobileWidth={12}
                                    width={6}
                                    bgColor={Variables.color.lightGrey}
                                    textAlign="left"
                                    alignItems="end"
                                    data={
                                        <>
                                            <BodyCopy
                                                dangerouslySetInnerHTML={{
                                                    __html: item.title,
                                                }}
                                            />
                                            <H2
                                                dangerouslySetInnerHTML={{
                                                    __html: item.copy.childMarkdownRemark.html.replace(
                                                        /h2>/g,
                                                        'p>'
                                                    ),
                                                }}
                                            />
                                        </>
                                    }
                                />
                            </Row>
                        )}

                        {idx == 3 && (
                            <ReverseRow>
                                <BlockText
                                    mobileWidth={12}
                                    width={6}
                                    bgColor={Variables.color.pink}
                                    textAlign="left"
                                    alignItems="end"
                                    data={
                                        <>
                                            <BodyCopy
                                                dangerouslySetInnerHTML={{
                                                    __html: item.title,
                                                }}
                                            />
                                            <H2
                                                dangerouslySetInnerHTML={{
                                                    __html: item.copy.childMarkdownRemark.html.replace(
                                                        /h2>/g,
                                                        'p>'
                                                    ),
                                                }}
                                            />
                                        </>
                                    }
                                />
                                <BlockImage
                                    mobileWidth={12}
                                    width={6}
                                    bgImage={BannerAmenities}
                                />
                            </ReverseRow>
                        )}
                        {idx == 4 && (
                            <Row>
                                <BlockImage
                                    mobileWidth={12}
                                    width={6}
                                    bgImage={BannerDoorStep}
                                />
                                <BlockText
                                    mobileWidth={12}
                                    width={6}
                                    bgColor={Variables.color.lightGrey}
                                    textAlign="left"
                                    alignItems="end"
                                    data={
                                        <>
                                            <BodyCopy
                                                dangerouslySetInnerHTML={{
                                                    __html: item.title,
                                                }}
                                            />
                                            <H2
                                                dangerouslySetInnerHTML={{
                                                    __html: item.copy.childMarkdownRemark.html.replace(
                                                        /h2>/g,
                                                        'p>'
                                                    ),
                                                }}
                                            />
                                        </>
                                    }
                                />
                            </Row>
                        )}
                        {idx == 5 && (
                            <ReverseRow
                                border
                                style={{
                                    backgroundColor: Variables.color.pink,
                                }}
                            >
                                <MapBlockText
                                    mobileWidth={12}
                                    width={6}
                                    bgColor={Variables.color.pink}
                                    textAlign="center"
                                    alignItems="center"
                                    data={
                                        <>
                                            <StyledH2
                                                dangerouslySetInnerHTML={{
                                                    __html: item.title,
                                                }}
                                            />
                                            <BodyCopy
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        item.copy
                                                            .childMarkdownRemark
                                                            .html,
                                                }}
                                            />
                                        </>
                                    }
                                />

                                <Col
                                    xs={12}
                                    md={6}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <MapBlockImage src={AndermattMap} />
                                </Col>
                            </ReverseRow>
                        )}
                    </div>
                ))}
                <Row>
                    <BlockImage
                        mobileWidth={12}
                        width={6}
                        bgImage={BannerOurVision}
                    />
                    <BlockText
                        mobileWidth={12}
                        width={6}
                        bgColor={Variables.color.lightGrey}
                        textAlign="left"
                        alignItems="end"
                        data={
                            <>
                                <BodyCopy
                                    dangerouslySetInnerHTML={{
                                        __html: teaserData.visionTitle,
                                    }}
                                />
                                <H2
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            teaserData.visionCopy
                                                .childMarkdownRemark.html,
                                    }}
                                />
                                <Button
                                    buttonLink={
                                        teaserData.visionButton.buttonLink
                                    }
                                    secondary
                                >
                                    {teaserData.visionButton.text}
                                </Button>
                            </>
                        }
                    />
                </Row>
                <ContactFormSection />
            </Wrapper>
        </Layout>
    )
}

export default TeaserTemplate
